/**
 * WeeklyPerformance Component
 *
 * Stacked bar chart showing weekly sales broken down by customer or product.
 * Fixed 1-week view with left/right arrow navigation. Week starts on Monday.
 */

import { useState, useEffect, useMemo } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { DashboardTabs } from '@/components/ui/dashboard-tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { BarChart3, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'
import supabase from '../../supabase-client'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// Product colours — consistent with DemandByDayStackedChart
const PRODUCT_COLOURS = {
  "Lion's Mane": '#F54A00',
  'Grey Oyster': '#009689',
  'Coral Tooth': '#6366F1',
  'Pink Oyster': '#EC4899',
  'Yellow Oyster': '#F59E0B',
  'King Oyster': '#8B5CF6',
  'Shiitake': '#10B981',
  'Chestnut': '#78716C'
}

const FALLBACK_COLOURS = [
  '#FF6B2C', '#00B8A9', '#818CF8', '#F472B6',
  '#FBBF24', '#A78BFA', '#34D399', '#A8A29E'
]

// Customer colours
const CUSTOMER_COLOURS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f59e0b',
  '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'
]

function getMonday(date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  const day = d.getDay()
  // getDay(): 0=Sun, 1=Mon ... 6=Sat → shift so Mon=0
  const diff = day === 0 ? 6 : day - 1
  d.setDate(d.getDate() - diff)
  return d
}

function formatDateRange(monday) {
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)

  const fmt = (d) =>
    d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

  const yearSuffix = sunday.toLocaleDateString('en-GB', { year: 'numeric' })
  return `${fmt(monday)} – ${fmt(sunday)} ${yearSuffix.split(' ').pop()}`
}

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)

// Combine one or more { customer → kg } maps into a single { customer → kg } map
const mergeCustomerQty = (...maps) => {
  const merged = {}
  for (const map of maps) {
    if (!map) continue
    for (const [name, kg] of Object.entries(map)) {
      merged[name] = (merged[name] || 0) + (kg || 0)
    }
  }
  return merged
}

// Turn a { customer → kg } map into [ [name, kg], ... ] sorted by kg descending,
// dropping zero/near-zero entries.
const breakdownRows = (map) =>
  Object.entries(map || {})
    .filter(([, kg]) => kg > 0.001)
    .sort((a, b) => b[1] - a[1])

/**
 * A Table-view number that reveals its per-customer breakdown on hover (desktop only).
 * Renders a plain dash for zero values, and a plain number on mobile or when there's
 * no breakdown to show.
 */
function BreakdownCell({ value, rows, title, isDesktop }) {
  if (!(value > 0)) return '—'

  const text = value.toFixed(1)
  if (!isDesktop || !rows?.length) return text

  return (
    <HoverCard openDelay={120} closeDelay={60}>
      <HoverCardTrigger asChild>
        <span className="cursor-default hover:underline decoration-dotted decoration-gray-300 underline-offset-4">
          {text}
        </span>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-56 p-3 bg-white">
        <h5 className="text-xs font-semibold text-gray-900 mb-2">{title}</h5>
        <div className="space-y-1.5">
          {rows.map(([name, kg]) => (
            <div key={name} className="flex justify-between text-xs">
              <span className="text-muted-foreground truncate mr-3">{name}</span>
              <span className="font-medium whitespace-nowrap">{kg.toFixed(1)}kg</span>
            </div>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}

export default function WeeklyPerformance() {
  const [loading, setLoading] = useState(true)
  const [weekOffset, setWeekOffset] = useState(0)
  const [salesData, setSalesData] = useState([])
  const [viewMode, setViewMode] = useState('customer')
  const [customerNames, setCustomerNames] = useState([])
  const [productNames, setProductNames] = useState([])
  const [familyNames, setFamilyNames] = useState([])
  // Nested breakdown for Table-view hover popovers: dateStr → family → { customer → kg }
  const [cellBreakdown, setCellBreakdown] = useState({})

  const isDesktop = useMediaQuery('(min-width: 768px)')

  const monday = useMemo(() => {
    const m = getMonday(new Date())
    m.setDate(m.getDate() + weekOffset * 7)
    return m
  }, [weekOffset])

  const isCurrentWeek = weekOffset === 0

  useEffect(() => {
    fetchSalesData()
  }, [monday])

  const fetchSalesData = async () => {
    try {
      setLoading(true)

      const startDate = new Date(monday)
      const endDate = new Date(monday)
      endDate.setDate(endDate.getDate() + 6)

      const startStr = toLocalDateString(startDate)
      const endStr = toLocalDateString(endDate)

      const { data: sales, error } = await supabase
        .from('sales')
        .select(`
          id,
          date,
          status,
          customers (name),
          branches (name),
          sale_items (
            quantity,
            price,
            products (
              strain,
              strain_family:strain_families(name)
            )
          )
        `)
        .gte('date', startStr)
        .lte('date', endStr)
        .order('date', { ascending: true })

      if (error) throw error

      // Initialise all 7 days
      const salesByDate = new Map()
      for (let i = 0; i < 7; i++) {
        const d = new Date(monday)
        d.setDate(monday.getDate() + i)
        const dateStr = toLocalDateString(d)
        salesByDate.set(dateStr, { date: dateStr, total: 0, orders: 0, label: DAY_LABELS[i] })
      }

      // Track breakdowns per day — both £ value and kg quantity
      const byDayCustomer = {}  // dateStr → { name: £total }
      const byDayProduct = {}   // dateStr → { strain: £total }
      const byDayCustomerQty = {} // dateStr → { name: kg }
      const byDayProductQty = {}  // dateStr → { strain: kg }
      const byDayFamilyQty = {}   // dateStr → { family: kg }
      const byDayFamilyCustomerQty = {} // dateStr → { family: { 'customer\0branch': kg } }
      const customerBranches = {} // customerName → Set(branchName) — to detect multi-branch customers
      // Track whether all sales for a segment are delivered
      const deliveredCustomer = {} // dateStr → { name: boolean }
      const deliveredProduct = {}  // dateStr → { strain: boolean }
      const allCustomers = new Set()
      const allProducts = new Set()
      const allFamilies = new Set()

      sales?.forEach(sale => {
        const existing = salesByDate.get(sale.date)
        if (!existing) return

        // Fresh mushrooms only — items with a product_id. Growing materials
        // and block formats live on different FKs and are excluded from this view.
        const freshItems = (sale.sale_items || []).filter(item => item.products)
        if (freshItems.length === 0) return

        const customerName = sale.customers?.name || 'Unknown'
        const branchName = sale.branches?.name || ''
        allCustomers.add(customerName)
        if (!customerBranches[customerName]) customerBranches[customerName] = new Set()
        if (branchName) customerBranches[customerName].add(branchName)

        if (!byDayCustomer[sale.date]) byDayCustomer[sale.date] = {}
        if (!byDayProduct[sale.date]) byDayProduct[sale.date] = {}
        if (!byDayCustomerQty[sale.date]) byDayCustomerQty[sale.date] = {}
        if (!byDayProductQty[sale.date]) byDayProductQty[sale.date] = {}
        if (!byDayFamilyQty[sale.date]) byDayFamilyQty[sale.date] = {}
        if (!byDayFamilyCustomerQty[sale.date]) byDayFamilyCustomerQty[sale.date] = {}
        if (!deliveredCustomer[sale.date]) deliveredCustomer[sale.date] = {}
        if (!deliveredProduct[sale.date]) deliveredProduct[sale.date] = {}

        const isDelivered = sale.status === 'delivered'

        // A segment is "delivered" only if ALL contributing sales are delivered
        if (!(customerName in deliveredCustomer[sale.date])) {
          deliveredCustomer[sale.date][customerName] = isDelivered
        } else {
          deliveredCustomer[sale.date][customerName] = deliveredCustomer[sale.date][customerName] && isDelivered
        }

        let saleTotal = 0
        let saleQty = 0
        freshItems.forEach(item => {
          const qty = item.quantity || 0
          const lineTotal = qty * (item.price || 0)
          saleTotal += lineTotal
          saleQty += qty

          const strain = item.products.strain || 'Unknown'
          const family = item.products.strain_family?.name
            || item.products.strain
            || 'Unknown'
          allProducts.add(strain)
          allFamilies.add(family)
          byDayProduct[sale.date][strain] = (byDayProduct[sale.date][strain] || 0) + lineTotal
          byDayProductQty[sale.date][strain] = (byDayProductQty[sale.date][strain] || 0) + qty
          byDayFamilyQty[sale.date][family] = (byDayFamilyQty[sale.date][family] || 0) + qty
          if (!byDayFamilyCustomerQty[sale.date][family]) byDayFamilyCustomerQty[sale.date][family] = {}
          const cbKey = `${customerName} ${branchName}`
          byDayFamilyCustomerQty[sale.date][family][cbKey] =
            (byDayFamilyCustomerQty[sale.date][family][cbKey] || 0) + qty

          if (!(strain in deliveredProduct[sale.date])) {
            deliveredProduct[sale.date][strain] = isDelivered
          } else {
            deliveredProduct[sale.date][strain] = deliveredProduct[sale.date][strain] && isDelivered
          }
        })

        byDayCustomer[sale.date][customerName] = (byDayCustomer[sale.date][customerName] || 0) + saleTotal
        byDayCustomerQty[sale.date][customerName] = (byDayCustomerQty[sale.date][customerName] || 0) + saleQty
        existing.total += saleTotal
        existing.orders += 1
      })

      // Attach breakdowns to each day entry
      const result = Array.from(salesByDate.values()).map(day => ({
        ...day,
        ...(byDayCustomer[day.date] || {}),
        ...Object.fromEntries(
          Object.entries(byDayProduct[day.date] || {}).map(([k, v]) => [`product_${k}`, v])
        ),
        // Quantity keys prefixed with qty_
        ...Object.fromEntries(
          Object.entries(byDayCustomerQty[day.date] || {}).map(([k, v]) => [`qty_${k}`, v])
        ),
        ...Object.fromEntries(
          Object.entries(byDayProductQty[day.date] || {}).map(([k, v]) => [`qty_product_${k}`, v])
        ),
        ...Object.fromEntries(
          Object.entries(byDayFamilyQty[day.date] || {}).map(([k, v]) => [`qty_family_${k}`, v])
        ),
        // Delivery status keys prefixed with del_
        ...Object.fromEntries(
          Object.entries(deliveredCustomer[day.date] || {}).map(([k, v]) => [`del_${k}`, v])
        ),
        ...Object.fromEntries(
          Object.entries(deliveredProduct[day.date] || {}).map(([k, v]) => [`del_product_${k}`, v])
        )
      }))

      // Sort customers by total descending for consistent legend ordering
      const customerTotals = {}
      for (const dayData of Object.values(byDayCustomer)) {
        for (const [name, val] of Object.entries(dayData)) {
          customerTotals[name] = (customerTotals[name] || 0) + val
        }
      }
      const sortedCustomers = Array.from(allCustomers).sort(
        (a, b) => (customerTotals[b] || 0) - (customerTotals[a] || 0)
      )

      // Sort products by total descending
      const productTotals = {}
      for (const dayData of Object.values(byDayProduct)) {
        for (const [name, val] of Object.entries(dayData)) {
          productTotals[name] = (productTotals[name] || 0) + val
        }
      }
      const sortedProducts = Array.from(allProducts).sort(
        (a, b) => (productTotals[b] || 0) - (productTotals[a] || 0)
      )

      // Sort families by total kg descending
      const familyTotals = {}
      for (const dayData of Object.values(byDayFamilyQty)) {
        for (const [name, val] of Object.entries(dayData)) {
          familyTotals[name] = (familyTotals[name] || 0) + val
        }
      }
      const sortedFamilies = Array.from(allFamilies)
        .sort((a, b) => (familyTotals[b] || 0) - (familyTotals[a] || 0))
        .sort((a, b) => (a === 'Shiitake') - (b === 'Shiitake'))

      // Resolve composite 'customer\0branch' keys to display labels now that branch
      // counts are known: append the branch only when the customer has more than one.
      const labelFor = (cbKey) => {
        const sep = cbKey.indexOf('\0')
        const cust = cbKey.slice(0, sep)
        const branch = cbKey.slice(sep + 1)
        const multiBranch = (customerBranches[cust]?.size || 0) > 1
        return multiBranch && branch && branch !== 'Main' ? `${cust} ${branch}` : cust
      }
      const relabelledBreakdown = {}
      for (const [date, families] of Object.entries(byDayFamilyCustomerQty)) {
        relabelledBreakdown[date] = {}
        for (const [family, custMap] of Object.entries(families)) {
          relabelledBreakdown[date][family] = Object.entries(custMap).reduce((acc, [cbKey, kg]) => {
            const label = labelFor(cbKey)
            acc[label] = (acc[label] || 0) + kg
            return acc
          }, {})
        }
      }

      setSalesData(result)
      setCellBreakdown(relabelledBreakdown)
      setCustomerNames(sortedCustomers)
      setProductNames(sortedProducts)
      setFamilyNames(sortedFamilies)
    } catch (error) {
      console.error('Error fetching sales data:', error)
    } finally {
      setLoading(false)
    }
  }

  const totalSales = useMemo(() =>
    salesData.reduce((sum, day) => sum + day.total, 0),
    [salesData]
  )

  const totalOrders = useMemo(() =>
    salesData.reduce((sum, day) => sum + day.orders, 0),
    [salesData]
  )

  const totalKg = useMemo(() =>
    salesData.reduce((sum, day) => {
      const dayKg = Object.entries(day)
        .filter(([k]) =>
          k.startsWith('qty_')
          && !k.startsWith('qty_product_')
          && !k.startsWith('qty_family_')
        )
        .reduce((s, [, v]) => s + (v || 0), 0)
      return sum + dayKg
    }, 0),
    [salesData]
  )

  const getProductColour = (product, index) =>
    PRODUCT_COLOURS[product] || FALLBACK_COLOURS[index % FALLBACK_COLOURS.length]

  const getCustomerColour = (index) =>
    CUSTOMER_COLOURS[index % CUSTOMER_COLOURS.length]

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const nonZero = payload.filter(entry => entry.value > 0)
      if (nonZero.length === 0) return null

      const dayData = payload[0].payload
      const total = nonZero.reduce((sum, entry) => sum + (entry.value || 0), 0)
      const totalQty = nonZero.reduce((sum, entry) => {
        const qtyKey = viewMode === 'customer'
          ? `qty_${entry.name}`
          : `qty_product_${entry.name}`
        return sum + (dayData[qtyKey] || 0)
      }, 0)

      return (
        <div className="bg-white border border-border rounded-lg shadow-lg p-3">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          <div className="space-y-1">
            {nonZero.map((entry, index) => {
              const qtyKey = viewMode === 'customer'
                ? `qty_${entry.name}`
                : `qty_product_${entry.name}`
              const qty = dayData[qtyKey] || 0
              return (
                <div key={index} className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div
                      className="w-3 h-3 rounded-sm shrink-0"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-muted-foreground truncate">{entry.name}</span>
                  </div>
                  <span className="font-medium whitespace-nowrap">{formatCurrency(entry.value)}</span>
                  <span className="text-muted-foreground whitespace-nowrap">{qty.toFixed(1)}kg</span>
                </div>
              )
            })}
            <div className="border-t border-border pt-1 mt-1 flex items-center gap-4 text-xs font-medium">
              <span className="flex-1">Total</span>
              <span className="whitespace-nowrap">{formatCurrency(total)}</span>
              <span className="text-muted-foreground whitespace-nowrap">{totalQty.toFixed(1)}kg</span>
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <Card className="border-0 shadow-none rounded-none sm:border sm:shadow-sm sm:rounded-xl">
      <CardHeader className="pb-3 px-3 sm:px-6">
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-muted-foreground" />
          Sales Performance
        </CardTitle>
        <div className="flex flex-col items-center gap-2 mt-2">
          <DashboardTabs
            tabs={[
              { value: 'customer', label: 'Customer' },
              { value: 'product', label: 'Product' },
              { value: 'table', label: 'Table' },
            ]}
            value={viewMode}
            onValueChange={setViewMode}
            size="small"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setWeekOffset(o => o - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[160px] text-center">
              {formatDateRange(monday)}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setWeekOffset(o => o + 1)}
              disabled={isCurrentWeek}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-3 sm:px-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 text-sm">
              <div>
                <span className="text-muted-foreground">Total:</span>{' '}
                <span className="font-semibold">{formatCurrency(totalSales)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Orders:</span>{' '}
                <span className="font-semibold">{totalOrders}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Avg/day:</span>{' '}
                <span className="font-semibold">
                  {formatCurrency(totalSales / 7)}
                </span>
              </div>
              <span className="text-border">|</span>
              <div>
                <span className="text-muted-foreground">Weight:</span>{' '}
                <span className="font-semibold">{totalKg.toFixed(1)} kg</span>
              </div>
              <div>
                <span className="text-muted-foreground">Avg/day:</span>{' '}
                <span className="font-semibold">{(totalKg / 7).toFixed(1)} kg</span>
              </div>
            </div>

            {/* Table view — daily breakdown of mushrooms ordered, grouped by strain family */}
            {viewMode === 'table' ? (
              familyNames.length === 0 ? (
                <div className="text-center text-muted-foreground py-8 text-sm">
                  No mushroom orders this week
                </div>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-left">Day</TableHead>
                        {familyNames.map(family => (
                          <TableHead key={family} className="text-right">{family}</TableHead>
                        ))}
                        <TableHead className="text-right font-semibold">Total (kg)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {salesData.map(day => {
                        const rowTotal = familyNames.reduce(
                          (sum, f) => sum + (day[`qty_family_${f}`] || 0), 0
                        )
                        return (
                          <TableRow key={day.date} className="hover:bg-gray-50">
                            <TableCell className="font-medium">{day.label}</TableCell>
                            {familyNames.map(family => {
                              const qty = day[`qty_family_${family}`] || 0
                              return (
                                <TableCell key={family} className="text-right">
                                  <BreakdownCell
                                    value={qty}
                                    rows={breakdownRows(cellBreakdown[day.date]?.[family])}
                                    title={`${family} · ${day.label}`}
                                    isDesktop={isDesktop}
                                  />
                                </TableCell>
                              )
                            })}
                            <TableCell className="text-right font-semibold">
                              <BreakdownCell
                                value={rowTotal}
                                rows={breakdownRows(mergeCustomerQty(...Object.values(cellBreakdown[day.date] || {})))}
                                title={`${day.label} · all products`}
                                isDesktop={isDesktop}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                      <TableRow className="bg-gray-50 font-semibold">
                        <TableCell>Total</TableCell>
                        {familyNames.map(family => {
                          const colTotal = salesData.reduce(
                            (sum, day) => sum + (day[`qty_family_${family}`] || 0), 0
                          )
                          return (
                            <TableCell key={family} className="text-right">
                              <BreakdownCell
                                value={colTotal}
                                rows={breakdownRows(mergeCustomerQty(
                                  ...Object.values(cellBreakdown).map(fams => fams[family])
                                ))}
                                title={`${family} · week`}
                                isDesktop={isDesktop}
                              />
                            </TableCell>
                          )
                        })}
                        <TableCell className="text-right">
                          <BreakdownCell
                            value={totalKg}
                            rows={breakdownRows(mergeCustomerQty(
                              ...Object.values(cellBreakdown).flatMap(fams => Object.values(fams))
                            ))}
                            title="Week total"
                            isDesktop={isDesktop}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )
            ) : (
            /* Chart */
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <BarChart data={salesData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `£${value}`}
                    width={60}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {viewMode === 'customer'
                    ? customerNames.map((name, i) => (
                        <Bar
                          key={name}
                          dataKey={name}
                          name={name}
                          stackId="a"
                          fill={getCustomerColour(i)}
                        >
                          {salesData.map((day, idx) => (
                            <Cell
                              key={idx}
                              fillOpacity={day[`del_${name}`] ? 1 : 0.33}
                            />
                          ))}
                        </Bar>
                      ))
                    : productNames.map((strain, i) => (
                        <Bar
                          key={strain}
                          dataKey={`product_${strain}`}
                          name={strain}
                          stackId="a"
                          fill={getProductColour(strain, i)}
                        >
                          {salesData.map((day, idx) => (
                            <Cell
                              key={idx}
                              fillOpacity={day[`del_product_${strain}`] ? 1 : 0.33}
                            />
                          ))}
                        </Bar>
                      ))
                  }
                </BarChart>
              </ResponsiveContainer>
            </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
